import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef } from "react";
import { useSmsChatMutations } from "@stockline/web";
import api from "@/app/api";
import { UUID } from "crypto";
import { PageOptions } from "@/app/types";
import type { SMSChatList, SMSChatResponse } from "@/app/api/types/sms";
import { useBulkMarkAsReadMutation } from "./_dexieRollback";

const DELTA_BUFFER_MS = 5_000;
const DELTA_INTERVAL_MS = 30_000;

// Query keys factory for SMS
// `chats()` is the prefix used by invalidations and matches every list variant
// (filtered or not). `chatsList(options)` is the leaf key used by the actual
// list query — options are explicitly normalized so calls with `undefined`
// fields hash to the same cache entry as calls with no args. Mirrors
// `whatsappKeys`; without normalization, `useSMSChats()` and
// `useSMSChats({ search: undefined })` produce different cache entries.
export const smsKeys = {
  all: ["sms"] as const,
  accounts: () => [...smsKeys.all, "accounts"] as const,
  chats: () => [...smsKeys.all, "chats"] as const,
  chatsList: (options?: {
    search?: string;
    includeArchived?: boolean;
    archivedOnly?: boolean;
  }) =>
    [
      ...smsKeys.chats(),
      "list",
      {
        search: options?.search,
        includeArchived: options?.includeArchived,
        archivedOnly: options?.archivedOnly,
      },
    ] as const,
  chat: (id: UUID) => [...smsKeys.all, "chat", id] as const,
  messages: (chatId: UUID | null) =>
    [...smsKeys.all, "messages", chatId] as const,
  message: (id: UUID) => [...smsKeys.all, "message", id] as const,
} as const;

export function useSMSAccounts() {
  return useQuery({
    queryKey: smsKeys.accounts(),
    queryFn: () => api.sms.getAccounts(),
  });
}

/**
 * Hook to fetch SMS chats with delta polling.
 *
 * - First fetch: full (no modified_since)
 * - Subsequent: delta only (modified_since = last server_time - 5s buffer)
 * - 0 items returned → same ref → no rerender
 * - Also picks up chats with new AI-created pre-orders
 */
export function useSMSChats(options?: {
  search?: string;
  includeArchived?: boolean;
  archivedOnly?: boolean;
}) {
  const queryClient = useQueryClient();

  const serverTimeRef = useRef<string | null>(null);
  const cachedResultRef = useRef<SMSChatList | null>(null);
  const cachedCountsRef = useRef<Record<string, number>>({});

  const queryFingerprint = `${options?.search}|${options?.includeArchived}|${options?.archivedOnly}`;
  const prevFingerprintRef = useRef(queryFingerprint);

  const fetchWithDelta = useCallback(async (): Promise<SMSChatList> => {
    // Reset on fingerprint change (search, archive toggle)
    if (prevFingerprintRef.current !== queryFingerprint) {
      serverTimeRef.current = null;
      cachedResultRef.current = null;
      cachedCountsRef.current = {};
      prevFingerprintRef.current = queryFingerprint;
    }

    let modifiedSince: string | undefined;
    if (serverTimeRef.current) {
      const since = new Date(
        new Date(serverTimeRef.current).getTime() - DELTA_BUFFER_MS,
      );
      modifiedSince = since.toISOString().slice(0, -1);
    }

    const result = await api.sms.getChats(
      { page: 1, size: 100 },
      {
        search: options?.search,
        includePreorderCounts: true,
        includeArchived: options?.includeArchived,
        archivedOnly: options?.archivedOnly,
        modifiedSince,
      },
    );

    serverTimeRef.current = result.server_time ?? serverTimeRef.current;

    let mergedResult: SMSChatList;

    if (modifiedSince && cachedResultRef.current) {
      if (result.items.length === 0) {
        // Nothing changed on server — use current query cache as source of truth
        // (it reflects optimistic updates from mutations like archive/read)
        const current = queryClient.getQueryData<SMSChatList>(
          smsKeys.chatsList(options),
        );
        if (current) {
          cachedResultRef.current = current;
          return current;
        }
        return cachedResultRef.current;
      }
      // Merge changed items into the current query cache (not stale ref)
      // to preserve any optimistic updates
      const baseItems =
        queryClient.getQueryData<SMSChatList>(smsKeys.chatsList(options))
          ?.items ?? cachedResultRef.current.items;
      const changedIds = new Set(result.items.map((c) => c.id));
      const kept = baseItems.filter((c) => !changedIds.has(c.id));
      const mergedItems = [...kept, ...result.items].sort(
        (a, b) =>
          new Date(b.last_message_date ?? 0).getTime() -
          new Date(a.last_message_date ?? 0).getTime(),
      );
      // Overlay pre-order counts
      const mergedCounts = { ...cachedCountsRef.current };
      if (result.unread_preorder_counts) {
        Object.assign(mergedCounts, result.unread_preorder_counts);
      }
      mergedResult = {
        ...cachedResultRef.current,
        items: mergedItems,
        unread_preorder_counts: mergedCounts,
      };
      cachedCountsRef.current = mergedCounts;
    } else {
      // Full fetch (first load or fingerprint change)
      mergedResult = result;
      cachedCountsRef.current = result.unread_preorder_counts ?? {};
    }

    cachedResultRef.current = mergedResult;

    // Per-chat unread counts are now seeded by usePreOrderStatusCounts.
    // The legacy /api/sms-chats response still bundles them but we ignore
    // that field — the V2 source filters soft-deleted pre-orders and shares
    // one cache for all 3 channels.

    return mergedResult;
  }, [
    queryFingerprint,
    options?.search,
    options?.includeArchived,
    options?.archivedOnly,
  ]);

  return useQuery({
    queryKey: smsKeys.chatsList(options),
    queryFn: fetchWithDelta,
    staleTime: DELTA_INTERVAL_MS,
    refetchInterval: () => (document.hidden ? 5 * 60_000 : DELTA_INTERVAL_MS),
    refetchIntervalInBackground: true,
  });
}

/**
 * Hook to fetch a single SMS chat by ID
 */
export function useSMSChat(chatId: UUID | null) {
  return useQuery({
    queryKey: smsKeys.chat(chatId!),
    queryFn: () => api.sms.getChat(chatId!),
    enabled: !!chatId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Legacy single-page message fetch — kept only for the frozen
// `recap/mobile/_legacy` surface. New code MUST use the offline-first
// `useSmsMessagesPaged` hook from `@stockline/web`.
export function useSMSMessages(chatId: UUID | null, pageOptions?: PageOptions) {
  return useQuery({
    queryKey: smsKeys.messages(chatId),
    queryFn: () =>
      api.sms.getChatMessages(chatId!, pageOptions || { page: 1, size: 50 }),
    enabled: !!chatId,
    staleTime: 1000 * 60 * 2,
  });
}

/**
 * Hook to send an SMS message
 */
export function useSendSMSMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      accountId,
      toNumber,
      content,
    }: {
      accountId: UUID;
      toNumber: string;
      content: string;
    }) => {
      return api.sms.sendMessage({ accountId, toNumber, content });
    },
    onSuccess: () => {
      // Only invalidate chat list (for last_message preview update)
      // Message cache will be updated by new-message detection
      queryClient.invalidateQueries({ queryKey: smsKeys.chats() });
    },
  });
}

/**
 * Hook to mark an SMS chat as read
 */
export function useMarkSMSChatAsRead() {
  const queryClient = useQueryClient();
  // Chat list UI reads `unreadCount` from the Dexie offline store (V2 sync),
  // not from this TanStack cache. Without an optimistic Dexie write the
  // unread badge stays visible until SSE round-trips. The TanStack writes
  // below stay so any legacy callers still see the optimistic value.
  const { markAsReadOptimistic } = useSmsChatMutations();

  return useMutation({
    mutationFn: (chatId: UUID) => api.sms.markChatAsRead(chatId),
    onMutate: async (chatId: UUID) => {
      // Only cancel chat list queries — never cancel in-flight message fetches
      await queryClient.cancelQueries({
        queryKey: smsKeys.chats(),
      });

      const previousQueries = queryClient.getQueriesData<SMSChatList>({
        queryKey: smsKeys.chats(),
      });

      // Optimistically set unread_count to 0 for this specific chat
      queryClient.setQueriesData<SMSChatList>(
        { queryKey: smsKeys.chats() },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            items: old.items.map((chat) =>
              chat.id === chatId ? { ...chat, unread_count: 0 } : chat,
            ),
          };
        },
      );

      let rollbackDexie: (() => Promise<void>) | null = null;
      try {
        rollbackDexie = await markAsReadOptimistic(chatId);
      } catch (err) {
        // Dexie unavailable — keep going with TanStack-only optimism so
        // onError can still revert the cache via `previousQueries`.
        console.error(
          "[useMarkSMSChatAsRead] Dexie optimistic write failed:",
          err,
        );
      }

      return { previousQueries, rollbackDexie };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      context?.rollbackDexie?.().catch((err) => {
        console.error("[useMarkSMSChatAsRead] Dexie rollback failed:", err);
      });
    },
  });
}

/**
 * Hook to mark all SMS chats as read. The chat list reads `unreadCount`
 * from the Dexie offline store, not from this TanStack cache — Dexie
 * bulk-flip is what actually clears the unread badges instantly; SSE
 * per-row updates reconcile `syncId` after. TanStack writes below stay
 * for any legacy callers.
 */
export function useMarkAllSMSChatsAsRead() {
  const { markAllAsReadOptimistic } = useSmsChatMutations();
  return useBulkMarkAsReadMutation<SMSChatList>({
    mutationFn: () => api.sms.markAllChatsAsRead(),
    queryKey: smsKeys.chats(),
    setUnreadZero: (old) => ({
      ...old,
      items: old.items.map((chat) => ({ ...chat, unread_count: 0 })),
    }),
    optimistic: markAllAsReadOptimistic,
    logPrefix: "useMarkAllSMSChatsAsRead",
  });
}

/**
 * Hook to update an SMS chat (name, partner, etc.)
 */
export function useUpdateSMSChat() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      chatId,
      displayName,
      partnerId,
    }: {
      chatId: UUID;
      displayName?: string;
      partnerId?: string | null;
    }) =>
      api.sms.updateChat(chatId, {
        display_name: displayName,
        partner_id: partnerId,
      }),
    onSuccess: (updatedChat) => {
      queryClient.setQueryData(
        smsKeys.chat(updatedChat.id as UUID),
        updatedChat,
      );
      queryClient.invalidateQueries({ queryKey: smsKeys.chats() });
    },
  });
}

// Archive mutations
//
// The chat list reads from Dexie via `useOfflineSmsChats()` and filters on
// `archived`/`enabled` locally. The TanStack cache here is consulted only
// by a few legacy surfaces. Without an optimistic Dexie write the UI
// freezes on archive/disable until the SSE round-trip
// (REST → FastAPI → RabbitMQ → V2 API → SSE → Dexie, ~100–500ms) lands.
// `setArchivedOptimistic` / `setEnabledOptimistic` flip the Dexie row
// instantly; SSE then reconciles with the canonical server payload.
/**
 * Hook to archive an SMS chat (soft delete)
 */
export function useArchiveSMSChat() {
  const queryClient = useQueryClient();
  const { setArchivedOptimistic } = useSmsChatMutations();

  return useMutation({
    mutationFn: (chatId: UUID) => api.sms.archiveChat(chatId),
    onMutate: async (chatId: UUID) => {
      const rollback = await setArchivedOptimistic(String(chatId), true);

      // Only cancel chat list queries — never cancel in-flight message fetches
      await queryClient.cancelQueries({ queryKey: smsKeys.chats() });

      const previousQueries = queryClient.getQueriesData<SMSChatList>({
        queryKey: smsKeys.chats(),
      });

      // Optimistically remove the chat from list
      queryClient.setQueriesData<SMSChatList>(
        { queryKey: smsKeys.chats() },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            items: old.items.filter((chat) => chat.id !== chatId),
          };
        },
      );

      return { previousQueries, rollback };
    },
    onError: (_err, _chatId, context) => {
      if (context?.rollback) void context.rollback();
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
  });
}

/**
 * Hook to unarchive an SMS chat
 */
export function useUnarchiveSMSChat() {
  const queryClient = useQueryClient();
  const { setArchivedOptimistic } = useSmsChatMutations();

  return useMutation({
    mutationFn: (chatId: UUID) => api.sms.unarchiveChat(chatId),
    onMutate: async (chatId: UUID) => {
      const rollback = await setArchivedOptimistic(String(chatId), false);

      // Only cancel chat list queries — never cancel in-flight message fetches
      await queryClient.cancelQueries({ queryKey: smsKeys.chats() });

      const previousQueries = queryClient.getQueriesData<SMSChatList>({
        queryKey: smsKeys.chats(),
      });

      // Optimistically remove from archived view
      queryClient.setQueriesData<SMSChatList>(
        { queryKey: smsKeys.chats() },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            items: old.items.filter((chat) => chat.id !== chatId),
          };
        },
      );

      return { previousQueries, rollback };
    },
    onError: (_err, _chatId, context) => {
      if (context?.rollback) void context.rollback();
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
  });
}

/**
 * Hook to archive multiple SMS chats in batch
 */
export function useArchiveSMSChatsBatch() {
  const queryClient = useQueryClient();
  const { setArchivedOptimistic } = useSmsChatMutations();

  return useMutation({
    mutationFn: (chatIds: string[]) => api.sms.archiveChatsBatch(chatIds),
    onMutate: async (chatIds: string[]) => {
      const rollbacks = await Promise.all(
        chatIds.map((id) => setArchivedOptimistic(id, true)),
      );
      return { rollbacks };
    },
    onError: (_err, _chatIds, context) => {
      if (context?.rollbacks) {
        for (const rollback of context.rollbacks) {
          if (rollback) void rollback();
        }
      }
    },
    onSuccess: () => {
      // Invalidate all chat lists
      queryClient.invalidateQueries({ queryKey: smsKeys.chats() });
    },
  });
}

/**
 * Hook to disable an SMS chat (removes from list with cascade delete of messages/shares)
 */
export function useDisableSMSChat() {
  const queryClient = useQueryClient();
  const { setEnabledOptimistic } = useSmsChatMutations();

  return useMutation({
    mutationFn: (chatId: string) => api.sms.disableChatCascade(chatId),
    onMutate: async (chatId: string) => {
      const rollback = await setEnabledOptimistic(chatId, false);

      // Only cancel chat list queries — never cancel in-flight message fetches
      await queryClient.cancelQueries({ queryKey: smsKeys.chats() });

      const previousQueries = queryClient.getQueriesData<SMSChatList>({
        queryKey: smsKeys.chats(),
      });

      // Optimistically remove the chat from list
      queryClient.setQueriesData<SMSChatList>(
        { queryKey: smsKeys.chats() },
        (old) => {
          if (!old || !old.items) return old;
          return {
            ...old,
            items: old.items.filter((chat) => chat.id !== chatId),
          };
        },
      );

      return { previousQueries, rollback };
    },
    onError: (_err, _chatId, context) => {
      if (context?.rollback) void context.rollback();
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSuccess: (_data, chatId) => {
      // Remove the deleted chat's message cache
      queryClient.removeQueries({
        queryKey: smsKeys.messages(chatId as UUID),
      });
    },
  });
}
